import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, X } from "lucide-react";
import { fetchWithAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Living scope + red flags for one client (BUILD-SPECS Spec 2, migration 108).
 *
 * Every row here was written by a model (server/jobs/clientScopeItems.ts) from a Claire
 * transcript, which is why the verbatim quote is shown next to every item rather than
 * hidden behind a hover: the reviewer's job is to check the item against what was
 * actually said, and they cannot do that if the evidence is one click away.
 *
 * Read-only except Done / Wrong. Editing the text or the quote is deliberately not
 * offered — an edited quote is not evidence any more. Wrong is not a delete: the row
 * stays, so Sheena's weekly extraction review can see what the model got wrong.
 */

const KIND_ORDER = [
  "red_flag",
  "client_commitment",
  "firm_commitment",
  "open_question",
  "out_of_scope_request",
  "decision",
] as const;

const KIND_LABEL: Record<string, string> = {
  red_flag: "Red flags",
  client_commitment: "Client owes us",
  firm_commitment: "We owe the client",
  open_question: "Open questions",
  out_of_scope_request: "Out-of-scope requests",
  decision: "Decisions",
};

export interface ScopeItem {
  id: string;
  kind: string;
  text: string;
  owner: string | null;
  due_date: string | null;
  quote: string;
  transcript_id: string;
  red_flag_type: string | null;
  confidence: number | null;
  status: string;
  needs_review: boolean;
  created_at: string;
}

function ItemRow({ item }: { item: ScopeItem }) {
  const qc = useQueryClient();
  const gate = useMutation({
    mutationFn: (status: "done" | "wrong") =>
      fetchWithAuth(`/api/scope-items/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scope-items"] }),
  });

  const isFlag = item.kind === "red_flag";
  return (
    <div className={`border rounded-md p-2 space-y-1 text-xs ${isFlag ? "border-rose-300 bg-rose-50/50" : ""}`}>
      <div className="flex items-start gap-1.5">
        {isFlag && <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />}
        <span className="font-medium flex-1">{item.text}</span>
        {item.status === "mentioned_again" && (
          <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">
            raised again
          </span>
        )}
      </div>
      <div className="text-muted-foreground flex flex-wrap gap-x-2">
        {item.red_flag_type && <span className="font-medium text-rose-700">{item.red_flag_type}</span>}
        {item.owner && <span>owner: {item.owner}</span>}
        {item.due_date && <span>due {item.due_date}</span>}
        <span>{item.created_at.slice(0, 10)}</span>
        {item.confidence != null && <span>{(item.confidence * 100).toFixed(0)}% confident</span>}
      </div>
      <blockquote className="border-l-2 pl-2 text-muted-foreground italic">{item.quote}</blockquote>
      <div className="flex items-center gap-1 pt-0.5">
        <Button
          size="sm"
          variant="ghost"
          className="h-5 px-1.5 text-[11px] text-emerald-700"
          disabled={gate.isPending}
          onClick={() => gate.mutate("done")}
        >
          <Check className="w-3 h-3 mr-0.5" /> Done
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-5 px-1.5 text-[11px] text-rose-700"
          disabled={gate.isPending}
          onClick={() => gate.mutate("wrong")}
        >
          <X className="w-3 h-3 mr-0.5" /> Wrong
        </Button>
      </div>
    </div>
  );
}

function Group({ title, items }: { title: string; items: ScopeItem[] }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {title} · {items.length}
      </h3>
      {items.map((i) => (
        <ItemRow key={i.id} item={i} />
      ))}
    </div>
  );
}

export function ClientScopeItems({ accountId }: { accountId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["scope-items", accountId],
    queryFn: () =>
      fetchWithAuth<{ items: ScopeItem[] }>(`/api/accounts/${accountId}/scope-items`).then((r) => r.items),
    retry: false,
  });

  if (isLoading) return <p className="text-xs text-muted-foreground p-2">Loading…</p>;
  if (error) return <p className="text-xs text-rose-700 p-2">{(error as Error).message}</p>;

  const items = data ?? [];
  if (!items.length) {
    return (
      <p className="text-xs text-muted-foreground p-2">
        No open scope items. They appear here after a <code>client</code>-tagged meeting is extracted.
      </p>
    );
  }

  // Low-confidence items are quarantined into their own section rather than mixed into
  // the main list — a 40%-confidence "client will provide payroll by Friday" read as fact
  // is worse than no item at all.
  const review = items.filter((i) => i.needs_review);
  const main = items.filter((i) => !i.needs_review);

  return (
    <div className="space-y-4 max-w-3xl">
      {KIND_ORDER.map((k) => (
        <Group key={k} title={KIND_LABEL[k]} items={main.filter((i) => i.kind === k)} />
      ))}
      {review.length > 0 && (
        <div className="space-y-1.5 border-t pt-3">
          <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
            Needs review · {review.length}
          </h3>
          <p className="text-[11px] text-muted-foreground">
            The extraction was under 60% confident on these. Check the quote before trusting them.
          </p>
          {review.map((i) => (
            <ItemRow key={i.id} item={i} />
          ))}
        </div>
      )}
    </div>
  );
}
